import { useState, useEffect, useMemo, type FC } from "react";
import { MdCheckCircle, MdHourglassEmpty, MdError, MdExpandMore, MdChevronRight, MdCalendarToday, MdFolder } from "react-icons/md";
import { deleteEmail, getArchiveHistory, saveEmailsToFolder } from "../../apiClient";
import { useAppContext } from "../contexts/AppContext";
import ContextMenu, { type ContextMenuItem } from "./ContextMenu";
import "../styles/ArchiveHistory.css";
import { ArchiveHistoryV2, ArchiveStatus } from "../../../../common/src/types/types";

type HistoryFilter = "all" | "failed";

interface FolderGroup {
    folderId: string;
    folderName: string;
    emails: ArchiveHistoryV2[];
}

interface DateGroup {
    dateKey: string;
    emails: ArchiveHistoryV2[];
    folders: FolderGroup[];
}

const ArchiveHistory: FC = () => {
    const { navigateToFolders, archiveStatusVersion, openEmailPreviewDialog } = useAppContext();
    const [entries, setEntries] = useState<ArchiveHistoryV2[]>([]);
    const [filter, setFilter] = useState<HistoryFilter>("failed");
    const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [contextMenu, setContextMenu] = useState<{ visible: boolean; x: number; y: number; email: ArchiveHistoryV2 | null }>({
        visible: false, x: 0, y: 0, email: null,
    });

    useEffect(() => {
        let isActive = true;
        const loadArchiveHistory = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const history = await getArchiveHistory();
                if (isActive) {
                    setEntries(history);
                }
            } catch (err) {
                if (isActive) {
                    setError("Failed to load archive history. Please try again.");
                    console.error("Error loading archive history:", err);
                }
            } finally {
                if (isActive) {
                    setIsLoading(false);
                }
            }
        };

        loadArchiveHistory();
        return () => {
            isActive = false;
        };
    }, [archiveStatusVersion]);

    const formatDateKey = (date: Date | string) => {
        const parsed = typeof date === "string" ? new Date(date) : date;
        return parsed.toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric" });
    };

    const formatTimestamp = (date: Date | string) => {
        const parsed = typeof date === "string" ? new Date(date) : date;
        return parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    };

    const filteredEntries = useMemo(
        () => (filter === "failed" ? entries.filter((e) => e.archiveStatus === "Failed") : entries),
        [entries, filter]
    );

    const dateGroups = useMemo<DateGroup[]>(() => {
        const dateMap = new Map<string, DateGroup>();
        for (const entry of filteredEntries) {
            const dateKey = formatDateKey(entry.archiveDate);
            let dateGroup = dateMap.get(dateKey);
            if (!dateGroup) {
                dateGroup = { dateKey, emails: [], folders: [] };
                dateMap.set(dateKey, dateGroup);
            }
            dateGroup.emails.push(entry);
        }

        for (const dateGroup of dateMap.values()) {
            const folderMap = new Map<string, FolderGroup>();
            for (const entry of dateGroup.emails) {
                let folderGroup = folderMap.get(entry.archiveFolder);
                if (!folderGroup) {
                    folderGroup = { folderId: entry.archiveFolder, folderName: entry.folderName, emails: [] };
                    folderMap.set(entry.archiveFolder, folderGroup);
                }
                folderGroup.emails.push(entry);
            }
            dateGroup.folders = Array.from(folderMap.values());
        }

        return Array.from(dateMap.values());
    }, [filteredEntries]);

    const toggleDate = (dateKey: string) => {
        setExpandedDates((prev) => {
            const next = new Set(prev);
            if (next.has(dateKey)) {
                next.delete(dateKey);
            } else {
                next.add(dateKey);
            }
            return next;
        });
    };

    const toggleFolder = (folderKey: string) => {
        setExpandedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(folderKey)) {
                next.delete(folderKey);
            } else {
                next.add(folderKey);
            }
            return next;
        });
    };

    const handleEmailContextMenu = (e: React.MouseEvent, email: ArchiveHistoryV2) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ visible: true, x: e.clientX, y: e.clientY, email });
    };

    const closeContextMenu = () => {
        setContextMenu({ visible: false, x: 0, y: 0, email: null });
    };

    const handleDeleteEmail = async (messageId: string) => {
        closeContextMenu();
        try {
            await deleteEmail(messageId);
            setEntries((prev) => prev.filter((e) => e.messageId !== messageId));
        } catch (err) {
            console.error("Failed to delete email", err);
        }
    };

    const handleRetryArchive = async (email: ArchiveHistoryV2) => {
        closeContextMenu();
        try {
            await saveEmailsToFolder(email.archiveFolder, [{ id: email.messageId, subject: email.subject, hasAttachments: false, internetMessageId: email.internetMessageId }], undefined, true);
            setEntries((prev) =>
                prev.map((e) =>
                    e.messageId === email.messageId ? { ...e, archiveStatus: "Pending" } : e
                )
            );
        } catch (err) {
            console.error("Failed to retry archive", err);
        }
    };

    const openPreview = (email: ArchiveHistoryV2) => {
        openEmailPreviewDialog({
            id: email.messageId,
            subject: email.subject,
            sender: "",
            recipient: "",
            allowBodyRemoval: false,
            body: "",
            date: new Date(email.archiveDate),
            hasAttachments: false,
            archiveStatus: email.archiveStatus,
            archiveFolderId: email.archiveFolder,
            archiveUserId: "",
            bodyRemoved: false,
            internetMessageId: email.internetMessageId
        });
    };

    const getStatusIcon = (status: ArchiveStatus) => {
        switch (status) {
            case "Archived":
                return <MdCheckCircle className="status-icon-completed" />;
            case "Pending":
                return <MdHourglassEmpty className="status-icon-pending" />;
            case "Failed":
                return <MdError className="status-icon-failed" />;
            default:
                return null;
        }
    };

    const getStatusText = (status: ArchiveStatus) => {
        switch (status) {
            case "Archived":
                return "Archived";
            case "Pending":
                return "In Progress";
            case "Failed":
                return "Failed";
            default:
                return status;
        }
    };

    return (
        <div className="archive-history-container">
            <div className="archive-history-header">
                <button className="archive-history-back-button" onClick={navigateToFolders}>
                    ← Back
                </button>
                <h2>Recent Archive Status</h2>
            </div>

            <div className="archive-history-mode-toggle">
                <button className={`archive-history-mode-pill ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>
                    All Entries
                </button>
                <button className={`archive-history-mode-pill ${filter === "failed" ? "active" : ""}`} onClick={() => setFilter("failed")}>
                    <MdError /> Failed Only
                </button>
            </div>

            <div className="archive-history-content">
                {isLoading && (
                    <div className="archive-history-loading">
                        <p>Loading archive history...</p>
                    </div>
                )}

                {error && !isLoading && (
                    <div className="archive-history-error">
                        <p>{error}</p>
                    </div>
                )}

                {!isLoading && !error && dateGroups.length === 0 && (
                    <div className="archive-history-empty">
                        <p>{filter === "failed" ? "No failed archives in the last 7 days." : "No emails archived in the last 7 days."}</p>
                    </div>
                )}

                {!isLoading && !error && dateGroups.length > 0 && (
                    <div className="archive-history-date-list">
                        {dateGroups.map((dateGroup) => {
                            const isDateExpanded = expandedDates.has(dateGroup.dateKey);
                            return (
                                <div key={dateGroup.dateKey} className="archive-history-date-group">
                                    <div className="archive-history-date-header" onClick={() => toggleDate(dateGroup.dateKey)}>
                                        {isDateExpanded ? <MdExpandMore className="archive-history-tree-toggle" /> : <MdChevronRight className="archive-history-tree-toggle" />}
                                        <MdCalendarToday className="archive-history-date-icon" />
                                        <span className="archive-history-date-label">{dateGroup.dateKey}</span>
                                        <span className="archive-history-count-badge">{dateGroup.emails.length} emails</span>
                                    </div>

                                    {isDateExpanded && (
                                        <div className="archive-history-folder-list">
                                            {dateGroup.folders.map((folderGroup) => {
                                                const folderKey = `${dateGroup.dateKey}::${folderGroup.folderId}`;
                                                const isFolderExpanded = expandedFolders.has(folderKey);
                                                return (
                                                    <div key={folderKey} className="archive-history-folder-group">
                                                        <div className="archive-history-folder-header" onClick={() => toggleFolder(folderKey)}>
                                                            {isFolderExpanded ? <MdExpandMore className="archive-history-tree-toggle" /> : <MdChevronRight className="archive-history-tree-toggle" />}
                                                            <MdFolder className="archive-history-folder-icon" />
                                                            <span className="archive-history-folder-label">{folderGroup.folderName}</span>
                                                            <span className="archive-history-count-badge">{folderGroup.emails.length} emails</span>
                                                        </div>

                                                        {isFolderExpanded && (
                                                            <div className="archive-history-emails-list">
                                                                {folderGroup.emails.map((email) => (
                                                                    <div
                                                                        key={email.messageId}
                                                                        className="archive-history-email-item"
                                                                        onContextMenu={(e) => handleEmailContextMenu(e, email)}
                                                                        onClick={() => openPreview(email)}
                                                                    >
                                                                        <div className="archive-history-email-main">
                                                                            <span className="archive-history-email-subject">{email.subject}</span>
                                                                            <span className="archive-history-email-time">{formatTimestamp(email.archiveDate)}</span>
                                                                        </div>
                                                                        <div className={`archive-history-email-status ${email.archiveStatus}`} title={`Email status: ${email.archiveStatus}`}>
                                                                            {getStatusIcon(email.archiveStatus)}
                                                                            <span className="archive-history-email-status-text">{getStatusText(email.archiveStatus)}</span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {(() => {
                const email = contextMenu.email;
                const items: ContextMenuItem[] = email
                    ? [
                        {
                            key: "remove",
                            label: "Remove from E-File",
                            title: "Removes email from the E-File Database and Queue if it hasn't been archived yet; the email will still exist in the actual folder.",
                            onClick: () => handleDeleteEmail(email.messageId),
                        },
                        ...(email.archiveStatus === "Failed" ? [
                            {
                                key: "retry",
                                label: "Retry Archiving",
                                title: "Attempt to archive this email again",
                                onClick: () => handleRetryArchive(email),
                            }
                        ] : [])
                    ]
                    : [];
                return (
                    <ContextMenu
                        visible={contextMenu.visible && !!email}
                        x={contextMenu.x}
                        y={contextMenu.y}
                        items={items}
                        onClose={closeContextMenu}
                    />
                );
            })()}
        </div>
    );
};

export default ArchiveHistory;
